import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { X } from "lucide-react";
import { byStatus, finApi, finErr, isArchived, money } from "@/lib/financeApi";
import { btnGold, field } from "@/components/nds/windows/finance/parts";

const iso = (d) => d.toISOString().slice(0, 10);
const lastWeek = () => { const d = new Date(); const sat = new Date(d); sat.setDate(d.getDate() - ((d.getDay() + 1) % 7 || 7)); const sun = new Date(sat); sun.setDate(sat.getDate() - 6); return [iso(sun), iso(sat)]; };
const n = (v) => Number(v) || 0;
const amt = (r, rate) => n(r.reg) * n(rate) + n(r.ot) * n(rate) * 1.5;
const COLS = [["reg", "Reg hrs"], ["ot", "OT hrs (1.5×)"], ["pay", "Pay rate $"], ["bill", "Bill rate $"]];

function LaborLine({ r, onChange, onRemove }) {
  return (
    <li className="py-2 space-y-1.5" data-testid={`labor-row-${r.id}`}>
      <div className="flex items-center gap-2"><span className="text-sm text-nex-silver font-medium truncate">{r.name}</span>
        <button type="button" onClick={onRemove} aria-label={`Remove ${r.name}`} data-testid={`labor-remove-${r.id}`} className="ml-auto text-nex-muted hover:text-red-300"><X className="h-3.5 w-3.5" /></button></div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {COLS.map(([k, label]) => <label key={k} className="space-y-0.5 block"><span className="nds-label-dim text-[9px]">{label}</span>
          <input type="number" min="0" step="0.01" value={r[k]} onChange={(e) => onChange({ ...r, [k]: e.target.value })} data-testid={`labor-${k}-${r.id}`} className={`${field} w-full text-right`} /></label>)}
      </div>
      <div className="flex flex-wrap gap-x-4 font-mono text-[11px]">
        <span className="text-nex-gold" data-testid={`labor-line-${r.id}`}>Bill to vendor {money(amt(r, r.bill))}</span>
        <span className="text-sky-200" data-testid={`labor-pay-amt-${r.id}`}>Resident pay {money(amt(r, r.pay))} · not on invoice</span>
      </div>
    </li>
  );
}

export default function LaborInvoiceForm({ onDone, onCancel }) {
  const [start, end] = lastWeek();
  const [vendors, setVendors] = useState([]);
  const [all, setAll] = useState([]);
  const [f, setF] = useState({ vendor_id: "", period_start: start, period_end: end, invoice_date: end });
  const [def, setDef] = useState({ pay: "", bill: "" });
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    finApi.get("/vendors", { active: true, type: "labor" }).then((d) => setVendors(d.vendors)).catch(() => {});
    finApi.get("/residents", { status: "all" }).then((d) => setAll(byStatus(d.residents))).catch(() => {});
  }, []);
  const pickVendor = async (vid) => {
    setF((x) => ({ ...x, vendor_id: vid }));
    if (!vid) return;
    try { const d = await finApi.get("/labor-invoices/prefill", { vendor_id: vid }); setDef({ pay: d.pay_rate ? String(d.pay_rate) : "", bill: d.bill_rate ? String(d.bill_rate) : "" }); }
    catch (e) { toast.error(finErr(e)); }
  };
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const addRes = (id) => { const r = all.find((x) => x.id === id); if (r) setRows((x) => [...x, { id, name: `${r.first_name} ${r.last_name}${isArchived(r) ? " (Archived)" : ""}`, reg: "", ot: "", pay: def.pay, bill: def.bill }]); };
  const bill = rows.reduce((s, r) => s + amt(r, r.bill), 0);
  const pay = rows.reduce((s, r) => s + amt(r, r.pay), 0);
  const ready = f.vendor_id && rows.length && rows.every((r) => n(r.pay) > 0 && n(r.bill) > 0 && n(r.reg) + n(r.ot) > 0);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const inv = await finApi.post("/labor-invoices", { ...f, lines: rows.map((r) => ({ resident_id: r.id, reg_hours: n(r.reg), ot_hours: n(r.ot), pay_rate: n(r.pay), bill_rate: n(r.bill) })) });
      toast.success(`Invoice #${inv.invoice_number} created · ${money(inv.total_amount)}`);
      onDone(inv.id);
    } catch (err) { toast.error(finErr(err)); } finally { setBusy(false); }
  };
  const L = (label, el) => <label className="space-y-1 block"><span className="nds-label-dim text-[9px]">{label}</span>{el}</label>;
  return (
    <form onSubmit={submit} data-testid="labor-inv-form" className="p-4 border border-nex-gold/30 rounded-sm space-y-3 bg-white/[0.02]">
      <div className="font-serif text-nex-silver">Resident Labor Invoice</div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {L("Vendor", <select required value={f.vendor_id} onChange={(e) => pickVendor(e.target.value)} data-testid="labor-vendor" className={`${field} w-full`}>
          <option value="">Select vendor…</option>{vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}</select>)}
        {L("Pay period start", <input type="date" required value={f.period_start} onChange={set("period_start")} data-testid="labor-start" className={`${field} w-full`} />)}
        {L("Pay period end", <input type="date" required value={f.period_end} onChange={set("period_end")} data-testid="labor-end" className={`${field} w-full`} />)}
        {L("Invoice date", <input type="date" required value={f.invoice_date} onChange={set("invoice_date")} data-testid="labor-date" className={`${field} w-full`} />)}
      </div>
      {f.vendor_id && <>
        <select value="" onChange={(e) => addRes(e.target.value)} data-testid="labor-add-resident" className={`${field} w-full`}>
          <option value="">+ Select a resident to add a line…</option>
          {[["Current residents", false], ["Archived residents", true]].map(([label, arch]) => (
            <optgroup key={label} label={label}>{all.filter((r) => isArchived(r) === arch && !rows.some((x) => x.id === r.id)).map((r) => <option key={r.id} value={r.id}>{r.first_name} {r.last_name}{arch ? " (Archived)" : ""}</option>)}</optgroup>))}</select>
        <ul className="divide-y divide-white/5" data-testid="labor-rows">
          {rows.map((r) => <LaborLine key={r.id} r={r} onChange={(x) => setRows((y) => y.map((z) => (z.id === r.id ? x : z)))} onRemove={() => setRows((y) => y.filter((z) => z.id !== r.id))} />)}
          {!rows.length && <li className="py-2 text-xs text-nex-muted" data-testid="labor-empty">No lines yet. Select a resident above.</li>}
        </ul>
      </>}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <span className="font-mono text-sm text-nex-gold" data-testid="labor-total">Bill to vendor {money(bill)}</span>
        <span className="font-mono text-xs text-sky-200" data-testid="labor-pay-total">Resident pay {money(pay)}</span>
        <span className="font-mono text-xs text-emerald-300" data-testid="labor-margin">Agency keeps {money(bill - pay)}</span>
        <button type="button" onClick={onCancel} data-testid="labor-cancel" className="ml-auto font-mono text-[10px] tracking-[0.2em] uppercase text-nex-muted">Cancel</button>
        <button type="submit" disabled={busy || !ready} data-testid="labor-submit" className={btnGold}>{busy ? "Creating…" : "Create invoice & PDF"}</button>
      </div>
      <p className="text-[11px] text-nex-muted">Pay rates are only shown here and in the invoice's line details. The vendor's PDF shows hours, bill rate and line bill only.</p>
    </form>
  );
}
